import React from 'react'
import {
  CATEGORY_OPTIONS,
  FORMAT_OPTIONS,
  GENDER_OPTIONS,
  LIFECYCLE_OPTIONS,
  Opt,
  PLAYER_MATCH_OPTIONS,
  ROUND_OPTIONS,
  SourceCode,
  SURFACE_OPTIONS,
} from '../../../constants/carouselOptions'
import {
  CompetitionFilterSelect,
  LookupProps,
  PlayerFilterSelect,
  SeasonFilterSelect,
  TagFilterSelect,
  VodTypeFilterSelect,
} from './carouselLookups'

export type FilterControl =
  | { kind: 'enum-multi'; options: readonly Opt[] }
  | { kind: 'enum-single'; options: readonly Opt[] }
  | { kind: 'number'; placeholder?: string; min?: number }
  | { kind: 'lookup'; Component: React.FC<LookupProps>; placeholder?: string }
  | { kind: 'start-window' }
  | { kind: 'display-window' }

export interface FilterDescriptor {
  key: string
  label: string
  // A function when the same key means different things to different sources
  // (see player_match), so the copy follows what the rail actually does.
  hint?: string | ((sources: SourceCode[]) => string | undefined)
  // Applies when at least one selected source is in this list.
  sources: SourceCode[]
  // Core filters render inline as soon as a relevant source is selected;
  // the rest live behind the "Add filter" menu.
  core?: boolean
  control: FilterControl
}

// Keys and their JSON shape must match the filter struct for the source: ACE's
// MatchFilters / ChannelFilters (apps/ace/internal/service/layout_dynamic.go) or
// VodFilters (layout_vod.go). Don't add one without the ACE side — a match
// filter with no field there is silently dropped, and the vod handler is
// stricter still: it rejects unknown keys outright, so a typo here surfaces as
// an INVALID_WIDGET_CONFIG error widget rather than a quietly wider rail.
export const FILTER_REGISTRY: FilterDescriptor[] = [
  { key: 'lifecycleState', label: 'Match status', sources: ['matches'], core: true,
    control: { kind: 'enum-multi', options: LIFECYCLE_OPTIONS } },

  // Shared by matches and vod. A vod rail resolves all of these through the
  // asset's linked match, so an asset with no match link matches none of them;
  // season_ids is the exception, read from the asset's own season links.
  { key: 'categories', label: 'Categories', sources: ['matches', 'vod'],
    control: { kind: 'enum-multi', options: CATEGORY_OPTIONS } },
  { key: 'competition_parent_ids', label: 'Tournaments', sources: ['matches', 'vod'],
    control: { kind: 'lookup', Component: CompetitionFilterSelect } },
  { key: 'season_ids', label: 'Seasons', sources: ['matches', 'vod'],
    control: { kind: 'lookup', Component: SeasonFilterSelect } },
  { key: 'formats', label: 'Match type', sources: ['matches', 'vod'],
    control: { kind: 'enum-multi', options: FORMAT_OPTIONS } },
  { key: 'surfaces', label: 'Surfaces', sources: ['matches', 'vod'],
    control: { kind: 'enum-multi', options: SURFACE_OPTIONS } },
  { key: 'rounds', label: 'Rounds', sources: ['matches', 'vod'],
    control: { kind: 'enum-multi', options: ROUND_OPTIONS } },
  { key: 'gender', label: 'Gender', sources: ['matches', 'vod'],
    control: { kind: 'enum-single', options: GENDER_OPTIONS } },
  // Shared by matches and vod: both take competitor ids, and a competitor is a
  // player or a doubles team, so either can be picked. They do not resolve
  // identically: matches reads the two sides, so an individual member of a
  // doubles team never matches, while vod reads tc_vod_player_map, which
  // ingestion fills with the team and both members.
  { key: 'player_ids', label: 'Players', sources: ['matches', 'vod'],
    control: { kind: 'lookup', Component: PlayerFilterSelect } },
  // Two hints because AND means different things per source: matches reads one
  // side against the other (head-to-head), while a VOD has no sides, so AND can
  // only mean every listed competitor appears on the asset. For two doubles
  // partners the two are opposites.
  { key: 'player_match', label: 'Player match', sources: ['matches', 'vod'],
    hint: (sources) => sources.includes('vod') && !sources.includes('matches')
      ? 'OR: any of these appear. AND: all of them appear.'
      : 'OR: a player on either side. AND: one on each side (head-to-head).',
    control: { kind: 'enum-single', options: PLAYER_MATCH_OPTIONS } },
  { key: 'start_window', label: 'Start window', sources: ['matches'],
    control: { kind: 'start-window' } },

  // --- vod ---
  // Catalog-backed (tc_vod_type via GET /vod/types), not a hardcoded enum, so
  // activating or renaming a type needs no CMS change.
  { key: 'vod_types', label: 'Content type', sources: ['vod'], core: true,
    control: { kind: 'lookup', Component: VodTypeFilterSelect } },
  { key: 'max_age_days', label: 'Max age (days)',
    hint: 'Only content whose display date is within this many days. Leave empty for no age limit — the rail then spans the whole catalog, capped by the limit.',
    sources: ['vod'],
    control: { kind: 'number', placeholder: 'e.g. 30', min: 1 } },
  // The date counterpart to max_age_days: a fixed range rather than a rolling
  // one, for a rail pinned to a past event. Named apart from the matches
  // start_window because it reads the display date, not a match's start time.
  // Both bound display_date, and setting both narrows to their overlap.
  { key: 'display_window', label: 'Display window',
    hint: 'Only content whose display date falls in this range.',
    sources: ['vod'],
    control: { kind: 'display-window' } },

  // --- merchandising tags ---
  // Two descriptors for one idea, because the two handlers spell the key
  // differently: VodFilters.TagKeys is `tag_keys` (layout_vod.go) and
  // ChannelFilters.Tags is `tags` (layout_dynamic.go). The vod handler accepts
  // both spellings and unions them, so either would work there, but each source
  // is authored with the key its own handler names to keep saved configs
  // readable. When ACE unifies, these collapse to one entry plus a metadata
  // migration.
  { key: 'tag_keys', label: 'Tags', hint: 'Any-of: content carrying any of these tag keys',
    sources: ['vod'],
    control: { kind: 'lookup', Component: TagFilterSelect } },
  { key: 'tags', label: 'Tags', hint: 'Any-of: channels carrying any of these tag keys',
    sources: ['channels'],
    control: { kind: 'lookup', Component: TagFilterSelect } },
]

export const descriptorsForSources = (sources: SourceCode[]): FilterDescriptor[] =>
  FILTER_REGISTRY.filter(d => d.sources.some(s => sources.includes(s)))
